import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  Card,
  CardBody,
  Content,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { PlusCircleIcon, TrashIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useDnsEndpoints } from '../../../hooks/useDnsEndpoints';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { NameserverGlue } from '../../../types/models';
import type { ApiResponseUpdateDomainBinding } from '../../../types/api/ApiResponseDomainName';

// The stored registrar delegation, reusing the wire types from the DomainName module.
type RegistrationNameservers = Record<string, NameserverGlue | null>;

interface ConfigureNameserversModalProps {
  isOpen: boolean;
  onClose: () => void;
  domainId: number;
  apex: string; // domainName.name — for in-bailiwick checks
  initial: RegistrationNameservers | null; // domainName.registration_nameservers — seeds the rows
  currentDnsEndpointId: number | null; // domainName.dns_endpoint_id — current binding (skip a no-op re-bind)
  onConfigured: () => void; // parent refetch, called after a successful save
}

// The endpoint menu's placeholder maps to "no selection". FormSelect reports its value as a string,
// so the selected endpoint id is held as a string and parsed to a number for the PATCH body.
const NO_ENDPOINT = '';

// Editing model — an ordered list of rows (maps are unordered; we need add/remove + stable keys).
interface NsRow {
  key: string;
  hostname: string;
  ipv4: string;
  ipv6: string;
}

let rowKeySeq = 0;
const nextKey = (): string => `ns-${++rowKeySeq}`;
const blankRow = (): NsRow => ({ key: nextKey(), hostname: '', ipv4: '', ipv6: '' });

// A delegation needs at least 2 nameservers; cap the form at 5 to match common registrar limits.
const MIN_NS = 2;
const MAX_NS = 5;

// Reuse ImportDomainModal's hostname regex.
const HOSTNAME_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
// Comprehensive IPv6 (incl. compressed + IPv4-mapped). The backend (filter_var) is authoritative.
const IPV6_RE =
  /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|::(ffff(:0{1,4})?:)?((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\.){3}(25[0-5]|(2[0-4]|1?[0-9])?[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\.){3}(25[0-5]|(2[0-4]|1?[0-9])?[0-9]))$/;

const normHost = (s: string): string => s.trim().toLowerCase().replace(/\.$/, '');

const isInBailiwick = (hostname: string, apex: string): boolean => {
  const h = normHost(hostname);
  const a = normHost(apex);
  if (!h || !a) return false;
  return h === a || h.endsWith('.' + a);
};

// Per-row admissibility, derived from the hostname + apex on every render.
type Admissibility = 'incomplete' | 'inbailiwick' | 'external';
const classify = (hostname: string, apex: string): Admissibility => {
  const h = normHost(hostname);
  if (!h || !HOSTNAME_RE.test(h)) return 'incomplete';
  return isInBailiwick(h, apex) ? 'inbailiwick' : 'external';
};

// Build the wire payload. In-bailiwick -> { ipv4?, ipv6? } (>=1 guaranteed by validation);
// out-of-bailiwick -> null. Empty-hostname rows are skipped.
const buildPayload = (rows: NsRow[], apex: string): RegistrationNameservers => {
  const map: RegistrationNameservers = {};
  for (const row of rows) {
    const host = normHost(row.hostname);
    if (!host) continue;
    if (isInBailiwick(host, apex)) {
      const glue: NameserverGlue = {};
      if (row.ipv4.trim()) glue.ipv4 = row.ipv4.trim();
      if (row.ipv6.trim()) glue.ipv6 = row.ipv6.trim();
      map[host] = glue;
    } else {
      map[host] = null;
    }
  }
  return map;
};

// Seed rows from the stored map, padded to at least MIN_NS blank rows so the 2-nameserver minimum
// is visible from the start. Reusable by the future provider-shortcut step to populate rows
// programmatically.
const rowsFromMap = (initial: RegistrationNameservers | null): NsRow[] => {
  const entries = initial ? Object.entries(initial) : [];
  const rows: NsRow[] = entries.map(([host, glue]) => ({
    key: nextKey(),
    hostname: host,
    ipv4: glue?.ipv4 ?? '',
    ipv6: glue?.ipv6 ?? '',
  }));
  while (rows.length < MIN_NS) rows.push(blankRow());
  return rows;
};

// Build rows from a DNS endpoint's published nameserver hostnames (the "fill from endpoint" shortcut).
// These are presented for delegation as-is; glue is left empty — they are normally out-of-bailiwick of
// the customer domain (e.g. ns1.ancalagon.be for example.com), which classify() treats as external (no
// glue). Capped at MAX_NS and padded to MIN_NS so the result is always a valid row count.
const rowsFromHostnames = (hostnames: string[]): NsRow[] => {
  const rows: NsRow[] = hostnames
    .slice(0, MAX_NS)
    .map((host) => ({ key: nextKey(), hostname: host, ipv4: '', ipv6: '' }));
  while (rows.length < MIN_NS) rows.push(blankRow());
  return rows;
};

type RowError = { hostname?: string; ipv4?: string; ipv6?: string; glue?: string };
type RowErrors = Record<string, RowError>;

interface ApiErrorBody {
  status?: 'success' | 'fail' | 'error';
  message?: string;
  data?: unknown;
}

function ConfigureNameserversModal({
  isOpen,
  onClose,
  domainId,
  apex,
  initial,
  currentDnsEndpointId,
  onConfigured,
}: ConfigureNameserversModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  // The endpoint catalog (shared + own) — fetched only while the modal is open (the hook gates on this).
  const {
    endpoints,
    loading: endpointsLoading,
    error: endpointsError,
    refetch: refetchEndpoints,
  } = useDnsEndpoints(isOpen);

  const [rows, setRows] = useState<NsRow[]>(() => rowsFromMap(initial));
  const [rowErrors, setRowErrors] = useState<RowErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The endpoint chosen in the menu (string id, or NO_ENDPOINT for the placeholder). Selecting one
  // fills the rows from its published nameservers and, on Save, (re)binds the domain to it.
  const [selectedEndpointId, setSelectedEndpointId] = useState<string>(NO_ENDPOINT);

  // Reset to the stored delegation whenever the modal (re)opens (mirrors ImportDomainModal). `initial`
  // is referentially stable from the parent (passed as `… ?? null`, which does not allocate), so this
  // runs effectively on open and never clobbers in-progress edits — keep that prop stable if reused.
  useEffect(() => {
    if (isOpen) {
      setRows(rowsFromMap(initial));
      setRowErrors({});
      setFormError(null);
      setIsSubmitting(false);
      setSelectedEndpointId(NO_ENDPOINT);
    }
  }, [isOpen, initial]);

  // The endpoint currently selected in the menu, resolved against the catalog (undefined for the
  // placeholder or while the catalog is still loading).
  const selectedEndpoint = endpoints.find((e) => String(e.id) === selectedEndpointId);
  // Name of the endpoint the domain is currently bound to, for context copy (null when unbound/unknown).
  const currentEndpointName =
    currentDnsEndpointId !== null ? (endpoints.find((e) => e.id === currentDnsEndpointId)?.name ?? null) : null;

  // Selecting an endpoint fills the rows below from its published nameservers (a destructive replace —
  // "use these nameservers"). An endpoint with no published nameservers leaves the rows untouched so the
  // user can still bind to it and type the delegation by hand. Re-selecting the placeholder just clears
  // the binding intent and keeps whatever rows are present.
  const onEndpointChange = (value: string) => {
    setSelectedEndpointId(value);
    setFormError(null);
    if (value === NO_ENDPOINT) {
      return;
    }
    const ep = endpoints.find((e) => String(e.id) === value);
    if (ep && ep.public_nameservers.length > 0) {
      setRows(rowsFromHostnames(ep.public_nameservers));
      setRowErrors({});
    }
  };

  // Helper copy under the menu, reflecting the current selection / binding. Forward-looking (no live
  // row count, which would drift after manual edits) and honest about the binding: the PATCH is a
  // DB-only re-bind that does NOT provision the zone, so it nudges to the DNS Zone tab when a bind
  // will happen, and says "already managed" (no bind) when the selected endpoint is the current one.
  let endpointHelp: string;
  if (selectedEndpoint) {
    const isAlreadyBound = selectedEndpoint.id === currentDnsEndpointId;
    const fillClause =
      selectedEndpoint.public_nameservers.length > 0
        ? `${selectedEndpoint.name}'s nameservers were filled in below — edit them if needed.`
        : `${selectedEndpoint.name} has no published nameservers — enter them manually below.`;
    const bindClause = isAlreadyBound
      ? `This domain is already managed by ${selectedEndpoint.name}.`
      : `Saving will bind this domain to ${selectedEndpoint.name}; you may then need to activate or repair its DNS zone from the DNS Zone tab.`;
    endpointHelp = `${fillClause} ${bindClause}`;
  } else if (currentEndpointName) {
    endpointHelp = `This domain is currently managed by ${currentEndpointName}. Pick an endpoint to fill the nameservers below and re-bind on save.`;
  } else {
    endpointHelp =
      'Pick one of your DNS endpoints to fill the nameservers below with its delegation and bind this domain to it on save.';
  }

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const clearRowError = (key: string, fields: Array<keyof RowError>) =>
    setRowErrors((prev) => {
      const cur = prev[key];
      if (!cur) return prev;
      const next = { ...cur };
      let changed = false;
      for (const f of fields) {
        if (next[f] !== undefined) {
          delete next[f];
          changed = true;
        }
      }
      return changed ? { ...prev, [key]: next } : prev;
    });

  // Hostname edit can move a row out of bailiwick; clear its glue only when the new hostname is
  // genuinely NOT a subdomain of the apex (isInBailiwick, which ignores HOSTNAME_RE completeness) so a
  // transient invalid keystroke on an in-bailiwick host doesn't discard glue the user already typed.
  // Disabled/external rows still never carry glue: the inputs are disabled unless classify==='inbailiwick',
  // and buildPayload re-derives null for any out-of-bailiwick host.
  const onHostnameChange = (key: string, value: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const keepGlue = isInBailiwick(value, apex);
        return { ...r, hostname: value, ipv4: keepGlue ? r.ipv4 : '', ipv6: keepGlue ? r.ipv6 : '' };
      }),
    );
    clearRowError(key, ['hostname', 'ipv4', 'ipv6', 'glue']);
  };

  const onIpv4Change = (key: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ipv4: value } : r)));
    clearRowError(key, ['ipv4', 'glue']);
  };

  const onIpv6Change = (key: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ipv6: value } : r)));
    clearRowError(key, ['ipv6', 'glue']);
  };

  const addRow = () => setRows((prev) => [...prev, blankRow()]);

  const removeRow = (key: string) => {
    setRows((prev) => (prev.length <= MIN_NS ? prev : prev.filter((r) => r.key !== key)));
    setRowErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = (): { rowErrors: RowErrors; formError: string | null } => {
    const re: RowErrors = {};
    let fe: string | null = null;
    const hostKeys = new Map<string, string[]>();
    let nonEmpty = 0;

    for (const row of rows) {
      const host = normHost(row.hostname);
      if (!host) continue; // empty-hostname rows are skipped (also skipped by buildPayload)
      nonEmpty++;
      const errs: RowError = {};

      if (!HOSTNAME_RE.test(host)) {
        errs.hostname = 'Enter a valid hostname (e.g. ns1.example.com).';
      } else {
        const seen = hostKeys.get(host) ?? [];
        seen.push(row.key);
        hostKeys.set(host, seen);

        const v4 = row.ipv4.trim();
        const v6 = row.ipv6.trim();
        if (isInBailiwick(host, apex)) {
          if (!v4 && !v6) {
            errs.glue = `In-bailiwick of ${apex} — at least one glue IP (IPv4 or IPv6) is required.`;
          }
          if (v4 && !IPV4_RE.test(v4)) errs.ipv4 = 'Enter a valid IPv4 address.';
          if (v6 && !IPV6_RE.test(v6)) errs.ipv6 = 'Enter a valid IPv6 address.';
        } else if (v4 || v6) {
          // External nameservers must not carry glue (safety net — inputs are disabled/auto-cleared).
          errs.glue = `Outside ${apex} — external nameservers must not have glue.`;
        }
      }

      if (Object.keys(errs).length > 0) re[row.key] = errs;
    }

    for (const keys of Array.from(hostKeys.values())) {
      if (keys.length > 1) {
        for (const k of keys) {
          re[k] = { ...(re[k] ?? {}), hostname: 'Duplicate nameserver hostname.' };
        }
      }
    }

    if (nonEmpty < MIN_NS) {
      fe = `At least ${MIN_NS} nameservers are required.`;
    } else if (nonEmpty > MAX_NS) {
      fe = `At most ${MAX_NS} nameservers are allowed.`;
    }

    return { rowErrors: re, formError: fe };
  };

  const handleSave = async () => {
    const { rowErrors: re, formError: fe } = validate();
    if (Object.keys(re).length > 0 || fe !== null) {
      setRowErrors(re);
      setFormError(fe);
      return;
    }

    setIsSubmitting(true);
    setRowErrors({});
    setFormError(null);

    // Whether to also (re)bind the domain's dns_endpoint_id: only when an endpoint is selected AND it
    // differs from the current binding (re-selecting the current endpoint just to fill is a no-op rebind).
    const endpointId = selectedEndpointId !== NO_ENDPOINT ? Number(selectedEndpointId) : null;
    const shouldBind = endpointId !== null && endpointId !== currentDnsEndpointId;
    const selectedName = selectedEndpoint?.name ?? 'the selected endpoint';

    try {
      // 1) Save the registrar delegation — the modal's primary action. For GoDaddy domains the hostnames
      //    are pushed to the registrar before persisting; on any failure nothing is persisted. This runs
      //    first so we never bind to an endpoint when the registrar config itself was rejected.
      const response = await apiFetch(`/datacenters/domain-names/${domainId}/nameservers`, {
        method: 'PUT',
        body: JSON.stringify({ registration_nameservers: buildPayload(rows, apex) }),
      });

      const responseBody = (await response.json().catch(() => null)) as ApiErrorBody | null;

      if (!response.ok || responseBody?.status !== 'success') {
        const data = responseBody?.data;
        if (
          data !== null &&
          typeof data === 'object' &&
          !Array.isArray(data) &&
          typeof (data as Record<string, unknown>).registration_nameservers === 'string'
        ) {
          setFormError((data as Record<string, string>).registration_nameservers);
        } else {
          setFormError(responseBody?.message ?? 'Could not save nameservers');
        }
        return; // modal stays open; nothing was bound
      }

      // 2) Optional binding (DB-only re-bind): set dns_endpoint_id to the chosen endpoint. The registrar
      //    config is already saved by now, so a binding failure is reported via a danger notification
      //    (the primary action succeeded) rather than trapping the user in the modal.
      if (shouldBind) {
        const bindResponse = await apiFetch(`/datacenters/domain-names/${domainId}`, {
          method: 'PATCH',
          body: JSON.stringify({ dns_endpoint_id: endpointId }),
        });
        const bindBody = (await bindResponse.json().catch(() => null)) as ApiResponseUpdateDomainBinding | null;

        if (!bindResponse.ok || bindBody?.status !== 'success') {
          addNotification({
            title: 'Nameservers saved, but endpoint not assigned',
            srTitle: 'Error alert',
            description:
              bindBody?.message ??
              `The registrar nameservers for ${apex} were saved, but the domain could not be bound to ${selectedName}.`,
            variant: 'danger',
          });
          setDrawerOpen(true);
          onConfigured();
          onClose();
          return;
        }
      }

      addNotification({
        title: 'Nameservers configured',
        srTitle: 'Success alert',
        // "Bound to" (not "managed by") — the PATCH only writes the binding; it does not create the
        // PowerDNS zone, so we point the user at the DNS Zone tab to activate/repair it if needed.
        description: shouldBind
          ? `The registrar nameservers for ${apex} were saved and the domain was bound to ${selectedName}. If it isn't hosting DNS there yet, activate or repair its zone from the DNS Zone tab.`
          : `The registrar nameservers for ${apex} were saved.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      onConfigured();
      onClose();
    } catch {
      setFormError('Network error — please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRow = (row: NsRow) => {
    const admissibility = classify(row.hostname, apex);
    const rowErr = rowErrors[row.key] ?? {};
    const glueDisabled = admissibility !== 'inbailiwick' || isSubmitting;

    let hintText: string;
    let hintVariant: 'default' | 'indeterminate' | 'error';
    if (rowErr.glue) {
      hintText = rowErr.glue;
      hintVariant = 'error';
    } else if (admissibility === 'inbailiwick') {
      hintText = `In-bailiwick of ${apex} — at least one glue IP is required.`;
      hintVariant = 'default';
    } else if (admissibility === 'external') {
      hintText = `Outside ${apex} — managed externally; no glue.`;
      hintVariant = 'indeterminate';
    } else {
      hintText = 'Enter the nameserver hostname.';
      hintVariant = 'default';
    }

    return (
      <Card isCompact key={row.key} className="pf-v6-u-mb-md">
        <CardBody>
          <Flex
            alignItems={{ default: 'alignItemsFlexStart' }}
            justifyContent={{ default: 'justifyContentSpaceBetween' }}
            spaceItems={{ default: 'spaceItemsSm' }}
          >
            <FlexItem grow={{ default: 'grow' }} style={{ minWidth: 0 }}>
              <FormGroup label="Nameserver hostname" isRequired fieldId={`ns-host-${row.key}`}>
                <TextInput
                  id={`ns-host-${row.key}`}
                  type="text"
                  value={row.hostname}
                  placeholder="ns1.example.com"
                  onChange={(_e, value) => onHostnameChange(row.key, value)}
                  validated={rowErr.hostname ? 'error' : 'default'}
                  isDisabled={isSubmitting}
                />
                {rowErr.hostname && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">{rowErr.hostname}</HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )}
              </FormGroup>
            </FlexItem>
            <FlexItem>
              <Button
                variant="plain"
                aria-label={`Remove nameserver${row.hostname.trim() ? ` ${row.hostname.trim()}` : ''}`}
                icon={<TrashIcon />}
                onClick={() => removeRow(row.key)}
                isDisabled={rows.length <= MIN_NS || isSubmitting}
              />
            </FlexItem>
          </Flex>

          <Flex spaceItems={{ default: 'spaceItemsMd' }} className="pf-v6-u-mt-sm">
            <FlexItem grow={{ default: 'grow' }} style={{ minWidth: 0 }}>
              <FormGroup label="IPv4 glue" fieldId={`ns-ipv4-${row.key}`}>
                <TextInput
                  id={`ns-ipv4-${row.key}`}
                  type="text"
                  value={row.ipv4}
                  placeholder="203.0.113.10"
                  onChange={(_e, value) => onIpv4Change(row.key, value)}
                  validated={rowErr.ipv4 ? 'error' : 'default'}
                  isDisabled={glueDisabled}
                />
                {rowErr.ipv4 && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">{rowErr.ipv4}</HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )}
              </FormGroup>
            </FlexItem>
            <FlexItem grow={{ default: 'grow' }} style={{ minWidth: 0 }}>
              <FormGroup label="IPv6 glue" fieldId={`ns-ipv6-${row.key}`}>
                <TextInput
                  id={`ns-ipv6-${row.key}`}
                  type="text"
                  value={row.ipv6}
                  placeholder="2001:db8::1"
                  onChange={(_e, value) => onIpv6Change(row.key, value)}
                  validated={rowErr.ipv6 ? 'error' : 'default'}
                  isDisabled={glueDisabled}
                />
                {rowErr.ipv6 && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">{rowErr.ipv6}</HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )}
              </FormGroup>
            </FlexItem>
          </Flex>

          <HelperText className="pf-v6-u-mt-sm">
            <HelperTextItem variant={hintVariant}>{hintText}</HelperTextItem>
          </HelperText>
        </CardBody>
      </Card>
    );
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={handleClose} aria-label="Configure nameservers">
      <ModalHeader title="Configure nameservers" />
      <ModalBody>
        {formError !== null && (
          <Alert variant="danger" isInline title="Could not save nameservers" className="pf-v6-u-mb-md">
            {formError}
          </Alert>
        )}

        <Content component="p" className="pf-v6-u-mb-md">
          Set the nameservers configured at the registrar for {apex} — provide {MIN_NS} to {MAX_NS}. In-bailiwick
          nameservers (the apex or a subdomain of it) require glue IPs; external nameservers carry none.
        </Content>

        <Form>
          {/* Shortcut: pick one of the client's managed DNS endpoints to auto-fill the delegation below
              and (on Save) bind the domain to it. Optional — manual entry still works. Hidden when the
              catalog is empty; a load failure degrades to a non-blocking warning. */}
          {endpointsLoading ? (
            <Bullseye className="pf-v6-u-mb-md">
              <Spinner size="md" aria-label="Loading DNS endpoints" />
            </Bullseye>
          ) : endpointsError !== null ? (
            <Alert
              variant="warning"
              isInline
              isPlain
              title="Couldn't load DNS endpoints"
              className="pf-v6-u-mb-md"
              actionLinks={<AlertActionLink onClick={() => void refetchEndpoints()}>Retry</AlertActionLink>}
            >
              You can still enter nameservers manually below.
            </Alert>
          ) : endpoints.length > 0 ? (
            <FormGroup label="Use a managed DNS endpoint" fieldId="ns-dns-endpoint">
              <FormSelect
                id="ns-dns-endpoint"
                value={selectedEndpointId}
                onChange={(_event, value) => onEndpointChange(value)}
                isDisabled={isSubmitting}
              >
                <FormSelectOption
                  value={NO_ENDPOINT}
                  label="Select an endpoint to fill the nameservers (optional)"
                  isPlaceholder
                />
                {endpoints.map((endpoint) => (
                  <FormSelectOption key={endpoint.id} value={String(endpoint.id)} label={endpoint.name} />
                ))}
              </FormSelect>
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>{endpointHelp}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          ) : null}

          {rows.map(renderRow)}
        </Form>

        <Button
          variant="link"
          icon={<PlusCircleIcon />}
          onClick={addRow}
          isDisabled={isSubmitting || rows.length >= MAX_NS}
          className="pf-v6-u-mt-sm"
        >
          Add nameserver
        </Button>
        {rows.length >= MAX_NS && (
          <HelperText className="pf-v6-u-mt-sm">
            <HelperTextItem variant="indeterminate">A delegation may have at most {MAX_NS} nameservers.</HelperTextItem>
          </HelperText>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={handleSave} isLoading={isSubmitting} isDisabled={isSubmitting}>
          Save
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ConfigureNameserversModal;
